import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, Send, Trash2 } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextAreaField } from '../../components/ui/Field.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';

const PAYMENT_LABEL = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  netbanking: 'Net banking',
  cheque: 'Cheque',
  adjust_against_sale: 'Adjusted against a sale invoice',
};

const STATUS_BADGE = {
  draft: 'border-warning text-warning',
  issued: 'border-success text-success',
  cancelled: 'border-danger text-danger',
};

const STATUS_LABEL = { draft: 'Draft', issued: 'Issued', cancelled: 'Cancelled' };

export default function GoldPurchaseViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState(null);

  const { data: purchase, isLoading } = useQuery({
    queryKey: ['gold-purchases', id],
    queryFn: () => api.get(`/gold-purchases/${id}`),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/gold-purchases/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gold-purchases'] });
      setCancelOpen(false);
    },
    onError: (err) => setCancelError(err instanceof ApiClientError ? err.message : 'Could not cancel this bill.'),
  });

  const issueMutation = useMutation({
    mutationFn: () => api.post(`/gold-purchases/${id}/issue`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gold-purchases'] }),
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not issue this bill.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/gold-purchases/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gold-purchases'] });
      navigate('/gold-purchases');
    },
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not delete this draft.'),
  });

  if (isLoading || !purchase) return null;

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/gold-purchases" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{purchase.bill_no ?? 'Draft bill'}</h1>
          <p className="text-sm text-muted">{new Date(purchase.bill_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        <span className={`ml-auto rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${STATUS_BADGE[purchase.status]}`}>
          {STATUS_LABEL[purchase.status]}
        </span>
      </div>

      {purchase.status === 'draft' ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          This bill hasn&rsquo;t been issued yet — it has no bill number and hasn&rsquo;t affected stock. Issue it to
          finalise, or delete it if it was a mistake.
        </p>
      ) : null}

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Purchased from</h2>
          <p className="font-medium">{purchase.customer_name}</p>
          {purchase.customer_mobile ? <p className="text-sm text-muted">{purchase.customer_mobile}</p> : null}
          {purchase.customer_address ? <p className="text-sm text-muted">{purchase.customer_address}</p> : null}
          {purchase.seller_is_registered ? <p className="text-sm text-muted">Registered dealer{purchase.seller_gstin ? ` — GSTIN ${purchase.seller_gstin}` : ''}</p> : null}
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Payment</h2>
          <p className="font-medium">{PAYMENT_LABEL[purchase.payment_mode] ?? purchase.payment_mode}</p>
          <p className="text-sm text-muted">Purity: {purchase.purity_label || '—'}</p>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">Particulars</th>
                <th className="py-2 pr-3 text-right">Gross wt.</th>
                <th className="py-2 pr-3 text-right">Net wt.</th>
                <th className="py-2 pr-3 text-right">Rate/g</th>
                <th className="py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {purchase.items.map((item) => (
                <tr key={item.id} className="tabular border-b border-border last:border-0">
                  <td className="py-2 pr-3">
                    {item.particulars}
                    {item.remarks ? <span className="text-muted"> · {item.remarks}</span> : null}
                  </td>
                  <td className="py-2 pr-3 text-right">{formatWeight(item.gross_weight)}</td>
                  <td className="py-2 pr-3 text-right">{formatWeight(item.net_weight)}</td>
                  <td className="py-2 pr-3 text-right">{formatINR(item.rate)}</td>
                  <td className="py-2 text-right font-medium">{formatINR(item.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card ml-auto w-full max-w-sm p-5">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Total gross weight</dt><dd className="tabular">{formatWeight(purchase.total_gross_weight)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Total net weight</dt><dd className="tabular">{formatWeight(purchase.total_net_weight)}</dd></div>
          <div className="mt-1 flex justify-between border-t border-border pt-2 text-base font-semibold text-primary"><dt>Amount</dt><dd className="tabular">{formatINR(purchase.total_amount)}</dd></div>
        </dl>
      </section>

      {actionError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        {purchase.status === 'draft' ? (
          <>
            <Button
              onClick={() => { setActionError(null); issueMutation.mutate(); }}
              loading={issueMutation.isPending}
              className="flex-1 sm:flex-none"
            >
              <Send size={17} aria-hidden="true" /> Issue bill
            </Button>
            <Button
              variant="danger"
              onClick={() => { setActionError(null); setDeleteOpen(true); }}
              className="flex-1 sm:flex-none"
            >
              <Trash2 size={17} aria-hidden="true" /> Delete draft
            </Button>
          </>
        ) : (
          <>
            <PdfActions
              path={`/gold-purchases/${id}/pdf`}
              documentLabel={`Gold purchase ${purchase.bill_no}`}
              fileBaseName={purchase.bill_no}
              onError={setActionError}
            />
            <ThermalPrintButton printPath={`/gold-purchases/${id}`} />
            {purchase.status !== 'cancelled' ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)} className="flex-1 sm:flex-none">
                <Ban size={17} aria-hidden="true" /> Cancel
              </Button>
            ) : null}
          </>
        )}
      </div>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this bill?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The bill number <b>{purchase.bill_no}</b> stays reserved and is never reused — cancelling records why, it
            doesn&rsquo;t erase the record.
          </p>
          <TextAreaField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          {cancelError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {cancelError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>Keep bill</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={cancelMutation.isPending}
              onClick={() => { setCancelError(null); cancelMutation.mutate(); }}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this draft?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            This draft never took a bill number and hasn&rsquo;t affected stock, so deleting it leaves no trace and no
            gap in the series. This can&rsquo;t be undone.
          </p>
          {actionError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {actionError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>Keep draft</Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <Trash2 size={16} aria-hidden="true" /> Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
